import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Row,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';

import LoadingSpiner from '../components/LoadingSpiner';
import {
  capitalizeWords,
  formatPhoneNumber,
  formatPrice,
} from '../components/capitalizeFunction';

import { usePagignationDevis } from '../../Api/queriesDevis';
import { useNavigate } from 'react-router-dom';
import { connectedUserBoutique } from '../Authentication/userInfos';
import ListPagination from '../components/ListPagination';

export default function DevisListe() {
  const [page, setPage] = useState(1);
  const limit = 35;
  // Afficher tous les Devis
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBoutique, setSelectedBoutique] = useState(null);

  /**
   * -----------------------------
   * MODIF (recherche pro + pagination cohérente + cache)
   * -----------------------------
   * Avant: on filtrait `devisData.results.data` côté frontend => recherche uniquement sur la page.
   * Maintenant:
   * - debounce 300ms
   * - on passe search + boutique au backend (filtre AVANT pagination)
   * - cache isolé via queryKey (corrigé dans le hook)
   */
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, selectedBoutique]);

  const {
    data: devisData,
    isLoading,
    error,
  } = usePagignationDevis(page, limit, debouncedSearchTerm, selectedBoutique);

  const filterDevis = useMemo(
    () => devisData?.results?.data || [],
    [devisData]
  );
  const totalDevis = devisData?.results?.total ?? 0;
  const totalPages = devisData?.results?.totalPages ?? 1;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <Breadcrumbs title='Devis' breadcrumbItem='Liste de Devis' />

          <Card className='p-4'>
            <div className=' d-flex align-items-center gap-3 mb-4 justify-content-between flex-wrap'>
              {/* Selectonner la boutique */}
              <div className='mb-3'>
                <h6>Filtrer par Boutique </h6>
                <select
                  value={selectedBoutique ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedBoutique(v === '' ? null : Number(v));
                  }}
                  className='form-select border border-dark rounded '
                  style={{ cursor: 'pointer' }}
                >
                  <option value=''>Toutes</option>
                  <option value={connectedUserBoutique ?? 0}>
                    {connectedUserBoutique ?? 0} - Ma Boutique
                  </option>
                  {connectedUserBoutique === 1 ? (
                    <option value='2'>Boutique - 2</option>
                  ) : connectedUserBoutique === 2 ? (
                    <option value='1'>Boutique - 1</option>
                  ) : (
                    <optgroup label='autres'>
                      <option value='1'>Boutique - 1</option>
                      <option value='2'>Boutique - 2</option>
                    </optgroup>
                  )}
                </select>
              </div>

              <div className='search-box me-2 d-flex align-items-center gap-2'>
                {searchTerm !== '' && (
                  <Button color='danger' onClick={() => setSearchTerm('')}>
                    <i className='fas fa-window-close'></i>
                  </Button>
                )}
                <input
                  type='text'
                  className='form-control search border border-dark rounded'
                  placeholder='Rechercher...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <h5>
              Total Enregistrée:{' '}
              <span className='text-info'>
                {formatPrice(totalDevis)}
              </span>
            </h5>
            <ListPagination
              page={devisData?.results?.page ?? page}
              totalPages={totalPages}
              onPageChange={setPage}
              className='mt-3 mb-0'
            />
          </Card>
          {error && (
            <div className='text-danger text-center'>
              Erreur de chargement des données
            </div>
          )}
          {isLoading && <LoadingSpiner />}
          {!error && filterDevis?.length === 0 && (
            <div className='mt-4 d-flex justify-content-center align-items-center flex-column'>
              <p className='text-center font-size-18 text-secondary'>
                Aucun Devis enregistré !
              </p>

              <Button
                color='info'
                className='add-btn mt-2'
                onClick={() => navigate('/newDevis')}
              >
                <i className='fas fa-plus align-center me-1'></i> Ajouter un
                Devis
              </Button>
            </div>
          )}

          {/* -----------------------------
              MODIF (performance):
              Avant: on rendait une "facture" complète pour chaque devis => DOM énorme et lent.
              Maintenant: on affiche une liste compacte (résumé) + bouton Détails.
              Le devis complet (items.produit) se charge sur la page détails.
           ----------------------------- */}
          {!error && !isLoading && filterDevis?.length > 0 && (
            <Row className='mt-3'>
              <Col lg={12}>
                <div className='table-responsive table-card'>
                  <table className='table align-middle table-nowrap table-hover'>
                    <thead className='table-light'>
                      <tr className='text-center'>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Tél</th>
                        <th>Adresse</th>
                        <th>Articles</th>
                        <th>Total</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody className='text-center'>
                      {filterDevis.map((dev) => (
                        <tr key={dev?._id}>
                          <td>
                            {new Date(dev?.createdAt).toLocaleDateString(
                              'fr-FR'
                            )}
                          </td>
                          <td>{capitalizeWords(dev?.fullName || '')}</td>
                          <td>{formatPhoneNumber(dev?.phoneNumber) || '---'}</td>
                          <td className='text-wrap'>
                            {capitalizeWords(dev?.adresse || '')}
                          </td>
                          <td>{formatPrice(dev?.itemsCount ?? 0)}</td>
                          <td className='text-info'>
                            {formatPrice(dev?.totalAmount)} F
                          </td>
                          <td>
                            <Button
                              color='info'
                              size='sm'
                              onClick={() =>
                                navigate(`/devis/devis_details/${dev?._id}`)
                              }
                            >
                              <i className='bx bx-show align-center me-1'></i>
                              Détails
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
}
